import { debounce } from 'lodash';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';

/* eslint-disable no-empty */
const COL_SIZE_REGEX = /col-md-(-?[0-9]+)/;
const LINE_THRESOLD = 45;
let defaultFaviconLink = '';

interface Placeholder {
  match: string;
  type: string;
}

interface PlaceholderResult {
  html: string;
  images: Array<Placeholder>;
}

interface WrappedDOMParser {
  parseFromString: (text: string, format: string) => Document;
}

// helper function to wrap native DOMParser in fallback implementations
// built with help from https://developer.mozilla.org/en-US/docs/Web/API/DOMParser#Creating_a_DOMParser
function getDomParser(): DOMParser {
  const proto = DOMParser.prototype,
    nativeParse = proto.parseFromString;

  // Firefox/Opera/IE throw errors on unsupported types
  try {
    const parser = new DOMParser();
    // WebKit returns null on unsupported types
    if (parser.parseFromString('', 'text/html')) {
      // text/html parsing is natively supported
      return parser;
    }
  } catch (ex) {}

  return {
    parseFromString: function (markup: string, type: DOMParserSupportedType) {
      if (/^\s*text\/html\s*(?:;|$)/i.test(type)) {
        const doc = document.implementation.createHTMLDocument('');
        if (markup.toLowerCase().indexOf('<!doctype') > -1) {
          doc.documentElement.innerHTML = markup;
        } else {
          doc.body.innerHTML = markup;
        }
        return doc;
      } else {
        return nativeParse.call(this, markup, type);
      }
    },
  };
}

// This method is a boiled down version of the jquery $(document).ready() method, in which it will invoke the provided
// callback immediately in the case that the document is already ready, and it will defer the invocation to the
// DOMContentLoaded event handler otherwise.
export function onDocReady(callback: () => any): void {
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    callback();
  } else {
    document.addEventListener('DOMContentLoaded', callback);
  }
}

export function getColumnSize(jqNode: any): number {
  if (jqNode.attr('class')) {
    const match = COL_SIZE_REGEX.exec(jqNode.attr('class'));
    if (match && match.length > 1) {
      return parseInt(match[1]);
    }
  }

  return 0; // Not sure of what value makes sense in this case
}

export function safeParseHTML(html: string): { body: Element[]; head: Element[] } {
  const parser = getDomParser();
  const doc = parser.parseFromString(html, 'text/html');

  const body = doc.body != null ? Array.from(doc.body.children) : [];
  const head = doc.head != null ? Array.from(doc.head.children) : [];

  return {
    body,
    // Some browsers may add empty title to the HEAD
    head: head.filter((e) => e.nodeName !== 'TITLE'),
  };
}

export function tryParseInt(input: string, defaultValue: number | null = null) {
  if (input) {
    const ret = parseInt(input);
    return !isNaN(ret) ? ret : defaultValue;
  }

  return undefined;
}

/**
 * Dispatches an event with IE10 compatbility
 */
export function dispatchEvent<T extends Event | CustomEvent>(
  target: EventTarget,
  name: string,
  params: Partial<T> = {}
) {
  if (typeof Event === 'function') {
    const ev = new Event(name, { bubbles: true, cancelable: true });
    Object.assign(ev, params);
    target.dispatchEvent(ev);
  } else {
    const ev = document.createEvent('HTMLEvents');
    ev.initEvent(name, true, true);
    Object.assign(ev, params);
    target.dispatchEvent(ev);
  }
}

/**
 * Dispatches a browser window resize event inside a `setTimeout` function with no delay
 */
export function forceResize(): void {
  setTimeout(() => {
    dispatchEvent(window, 'resize');
  }, 0);
}

export const debounceResize = debounce(($window?: EventTarget) => {
  dispatchEvent($window || window, 'resize');
}, 50); // if too much less than it could still end up firing multiple times

export function updateFavIcon(state = 'default', isAnalystStudio: boolean) {
  // We need to record the link to the first favicon past, in order to properly set it back after a load sequence completes
  if (defaultFaviconLink === '') {
    defaultFaviconLink = document.querySelector('link[rel="shortcut icon"]')?.getAttribute('href') ?? '';
  }

  const stateToIconMap: { [key: string]: string } = {
    succeeded: isAnalystStudio ? '/favicon-analyst-studio-success.png' : '/favicon-success.png',
    running: isAnalystStudio ? '/favicon-analyst-studio-in-progress.png' : '/favicon-in-progress.png',
    failed: isAnalystStudio ? '/favicon-analyst-studio-fail.png' : '/favicon-fail.png',
    cancelled: isAnalystStudio ? '/favicon-analyst-studio-fail.png' : '/favicon-fail.png',
    warning: isAnalystStudio ? '/favicon-analyst-studio-fail.png' : '/favicon-fail.png',
    default: defaultFaviconLink,
  };

  document.querySelector('link[rel="shortcut icon"]')?.setAttribute('href', stateToIconMap[state]);
}

export function wrapIntoMultiLine(content: string) {
  return content.replace(new RegExp(`(?![^\\n]{1,${LINE_THRESOLD}}$)([^\\n]{1,${LINE_THRESOLD}})\\s`, 'g'), '$1\n');
}

export function isChildOf(containerElement: Element, clickedTarget: HTMLElement | null): boolean {
  do {
    if (clickedTarget === containerElement) {
      // This is a click inside. Do nothing, just return.
      return true;
    }
    // Go up the DOM
    clickedTarget = clickedTarget ? clickedTarget.parentElement : null;
  } while (clickedTarget);
  return false;
}

export function testClipboardSupport($window: Window) {
  // Note that clipboard support will only be available in SSL, so currently cannot test locally
  return $window.navigator.clipboard != null && typeof $window.navigator.clipboard.writeText === 'function';
}

export function testSandboxSupport($window: Window) {
  return 'sandbox' in $window.document.createElement('iframe');
}

export function setIFrameSrc(iframe: HTMLIFrameElement, url: string) {
  const isRebrand = ThoughtspotIntegrationService.currOrgIntegrated();
  url += `&rebrand=${isRebrand}`;
  iframe.setAttribute('src', url);
}

export interface IFocusOptions {
  preventScroll?: boolean;
}

/**
 * Boolean indicating whether or not focus supports the preventScroll option.
 * Used to cache the result of `testFocusSupportsPreventScrollOption`
 */
let focusSupportsPreventScrollOption: any = null;

/**
 * @returns boolean
 * @description
 * Test whether or not this environment supports the preventScroll
 * option of https://developer.mozilla.org/en-US/docs/Web/API/HTMLOrForeignElement/focus
 *
 * `preventScroll` - A Boolean value indicating whether or not the browser should
 * scroll the document to bring the newly-focused element into view. A value of
 * false for preventScroll (the default) means that the browser will scroll the
 * element into view after focusing it. If preventScroll is set to true, no scrolling
 * will occur.
 *
 * Support detection ripped from:
 * https://github.com/calvellido/focus-options-polyfill/blob/master/index.js#L11-L29
 */
export function testFocusSupportsPreventScrollOption() {
  if (focusSupportsPreventScrollOption !== null) {
    return focusSupportsPreventScrollOption;
  }

  focusSupportsPreventScrollOption = false;

  try {
    const focusElem = document.createElement('div');
    focusElem.addEventListener(
      'focus',
      function (event) {
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
    focusElem.focus(
      <IFocusOptions>Object.defineProperty({}, 'preventScroll', {
        get() {
          focusSupportsPreventScrollOption = true;
        },
      })
    );
  } catch {}

  return focusSupportsPreventScrollOption;
}

/**
 * Resets the result cache for testing purposes
 */
testFocusSupportsPreventScrollOption.reset = () => {
  focusSupportsPreventScrollOption = null;
};

/**
 * @description Returns true if `value` is an Element node, false otherwise
 * @param value {any}
 * @returns {boolean}
 */
export const isElementNode = (value: any): boolean => !!(value?.nodeType === Node.ELEMENT_NODE);

/**
 * @description The isConnected read-only property of the Node interface returns a boolean
 *  indicating whether the node is connected (directly or indirectly) to the context object,
 *  for example the Document object in the case of the normal DOM, or the ShadowRoot in the
 *  case of a shadow DOM
 *
 * @param node {Node}
 * @returns {boolean} A Boolean that is true if the node is connected to its
 *  relevant context object, and false if not
 */
export const isNodeConnected = (node: Node): boolean => {
  const { isConnected } = node;

  return typeof isConnected === 'boolean'
    ? isConnected
    : // For IE10 and EdgeHTML
      !node.ownerDocument || !(node.ownerDocument.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_DISCONNECTED);
};

/**
 * @description Ensure that a text selection's range is contained within a node.
 *  If there is no selection, or the selection is contained within the node, we
 *  bail early. In all other cases the current selection will be dropped, and the
 *  children of the provided node will be added to the selection
 *
 *  Note: functionally equivalent to the css config `user-select: contain`
 *
 * @param node {Element} Element node that will contain the selection
 * @returns {void}
 */
export const containSelectionWithinElement = (node: Element): void => {
  // Bail early if this isn't an element node, or the node isn't in the doc
  if (!(isElementNode(node) && isNodeConnected(node))) {
    return;
  }

  const window = node.ownerDocument.defaultView;

  if (!window) {
    return;
  }

  const selection = window.getSelection();

  // Don't do work if there is no selection
  if (selection && String(selection) !== '') {
    const { commonAncestorContainer } = selection.getRangeAt(0);

    /**
     * If the common ancestor of the selection(s) is not contained within the
     * target node then the selection has escaped the desired boundary; reset
     * the selection to be within the provided node
     */
    if (!node.contains(commonAncestorContainer)) {
      selection.selectAllChildren(node);
    }
  }
};

/**
 * @description Similar to JQuery's .offset() method,
 * helps retrieve the offset coordinates for the specific element,
 * relative to the document, also includes the width and height.
 *
 * @param el {HTMLElement} HTMLElement whose offsets we have to calculate.
 * @returns an object that contains the offsets, width and height of the element.
 */
export const calculateOffset = (el: HTMLElement) => {
  const { width, height, top, left } = el.getBoundingClientRect();
  const docElem = document.documentElement;
  return {
    width,
    height,
    top: top + window.scrollY - docElem.clientTop,
    left: left + window.scrollX - docElem.clientLeft,
  };
};

/**
 * @description Retrieves the direct child element of a given parent element.
 * This function traverses up the DOM tree from the provided child element
 * until it finds the direct child of the specified parent element.
 *
 * @param {Element} parentElement - The parent element whose direct child is being sought.
 * @param {Element} childElement - The starting child element for traversal.
 * @returns The direct child element of the parent element, or null if no such element is found.
 */
export const getDirectChildElement = (parentElement: Element, childElement: Element): Element | null => {
  let directChild: Node = childElement;

  while (directChild.parentNode !== parentElement) {
    if (!directChild.parentNode || !parentElement) {
      return null;
    }

    directChild = directChild.parentNode;
  }

  return directChild as Element;
};

/**
 * @description Determines if a drag event's position is before the vertical midpoint of a specified element.
 * This function calculates the bounding rectangle of the element and compares the drag event's
 * client Y-coordinate with the vertical midpoint of the element.
 *
 * @param {DragEvent} event - The drag event containing the client Y-coordinate.
 * @param {Element} el - The element to compare the drag event's position against.
 * @returns true if the drag event's position is before the vertical midpoint of the element, otherwise false.
 */
export const isDragBeforeElement = (event: DragEvent, el: Element) => {
  const bounds = el.getBoundingClientRect();
  return event.clientY < bounds.top + bounds.height / 2;
};
